import React, { useState } from 'react';
import {
  Settings as SettingsIcon,
  User,
  Crown,
  CreditCard,
  Bell,
  Shield,
  LogOut,
  ExternalLink,
  Check,
} from 'lucide-react';
import { Card, Button } from '../components/ui';
import { useAuthStore, useQuotaStore, useServerStore, useUIStore } from '../store';
import { api, getDiscordAppSubscriptionUrl } from '../lib/api';

export function Settings() {
  const { user, logout } = useAuthStore();
  const { quota, hasAccess } = useQuotaStore();
  const { currentServer } = useServerStore();
  const { showToast } = useUIStore();

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await api.auth.logout();
      logout();
      localStorage.removeItem('auth_token');
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
      showToast('Failed to log out', 'error');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleUpgrade = () => {
    window.open(getDiscordAppSubscriptionUrl(), '_blank');
  };

  const handleManageServerSubscription = async () => {
    if (!currentServer) return;

    setIsManagingSubscription(true);
    try {
      const response = await api.subscriptions.getPortal(currentServer.id);
      if (response.success && response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      console.error('Failed to get portal:', error);
      showToast('Failed to open subscription portal', 'error');
    } finally {
      setIsManagingSubscription(false);
    }
  };

  return (
    <div className="animate-in max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-foretell-accent/20 flex items-center justify-center text-foretell-accent">
          <SettingsIcon size={20} />
        </div>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
          <p className="text-sm text-slate-400">Manage your account and preferences</p>
        </div>
      </div>

      {/* Profile Section */}
      <Card className="mb-6">
        <div className="flex items-center gap-4 mb-6">
          <User size={18} className="text-foretell-accent" />
          <h3 className="font-bold">Profile</h3>
        </div>

        {user ? (
          <div className="flex items-center gap-4">
            {user.avatar ? (
              <img
                src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png?size=64`}
                alt=""
                className="w-16 h-16 rounded-xl"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-foretell-accent/20 flex items-center justify-center">
                <User size={24} className="text-foretell-accent" />
              </div>
            )}
            <div>
              <div className="font-bold text-lg">{user.username}</div>
              <div className="text-sm text-slate-400">{user.email || 'No email connected'}</div>
              <div className="text-xs text-slate-500 mt-1">ID: {user.discordId}</div>
            </div>
          </div>
        ) : (
          <p className="text-slate-400">Sign in to view your profile</p>
        )}
      </Card>

      {/* Subscription Section */}
      <Card className="mb-6">
        <div className="flex items-center gap-4 mb-6">
          <Crown size={18} className="text-amber-400" />
          <h3 className="font-bold">Subscription</h3>
        </div>

        {hasAccess ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <Check size={20} className="text-amber-400" />
              <div>
                <div className="font-bold text-amber-400">Premium Active</div>
                <div className="text-sm text-slate-400">
                  You have unlimited access to all features
                </div>
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={handleUpgrade}
              icon={<ExternalLink size={16} />}
            >
              Manage Subscription
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="font-bold mb-1">Free Plan</div>
              <div className="text-sm text-slate-400 mb-3">
                {quota
                  ? `${quota.used}/${quota.limit} predictions used this month`
                  : 'Limited predictions per month'}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-400">
                  <Check size={14} className="text-emerald-400" />
                  {quota?.limit || 5} predictions per month
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Check size={14} className="text-emerald-400" />
                  Vote on predictions
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Check size={14} className="text-emerald-400" />
                  View all channels
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-gradient-to-r from-foretell-accent/20 to-purple-500/20 border border-foretell-accent/30">
              <div className="flex items-center gap-2 mb-1">
                <Crown size={16} className="text-amber-400" />
                <span className="font-bold">Premium</span>
              </div>
              <div className="text-sm text-slate-300 mb-3">
                Unlock unlimited predictions and exclusive features
              </div>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center gap-2 text-white">
                  <Check size={14} className="text-amber-400" />
                  Unlimited predictions
                </div>
                <div className="flex items-center gap-2 text-white">
                  <Check size={14} className="text-amber-400" />
                  Anonymous posting
                </div>
                <div className="flex items-center gap-2 text-white">
                  <Check size={14} className="text-amber-400" />
                  Edit & delete predictions
                </div>
                <div className="flex items-center gap-2 text-white">
                  <Check size={14} className="text-amber-400" />
                  Prediction history & analytics
                </div>
              </div>
              <Button
                variant="primary"
                onClick={handleUpgrade}
                icon={<ExternalLink size={16} />}
                className="w-full"
              >
                Upgrade to Premium
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Server Subscription */}
      {currentServer && (
        <Card className="mb-6">
          <div className="flex items-center gap-4 mb-6">
            <CreditCard size={18} className="text-foretell-accent" />
            <h3 className="font-bold">Server Subscription</h3>
          </div>

          {currentServer.subscription?.status === 'active' ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                <Check size={20} className="text-emerald-400" />
                <div>
                  <div className="font-bold text-emerald-400">Server Premium Active</div>
                  <div className="text-sm text-slate-400">
                    All members have unlimited access
                  </div>
                </div>
              </div>
              <Button
                variant="secondary"
                onClick={handleManageServerSubscription}
                loading={isManagingSubscription}
                icon={<ExternalLink size={16} />}
              >
                Manage Server Subscription
              </Button>
            </div>
          ) : (
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="font-bold mb-2">Unlock for your entire server</div>
              <div className="text-sm text-slate-400 mb-4">
                Give all members unlimited predictions and premium features
              </div>
              <Button
                variant="secondary"
                onClick={() => showToast('Server subscriptions coming soon', 'info')}
              >
                Learn More
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Danger Zone */}
      {user && (
        <Card className="border-rose-500/30">
          <div className="flex items-center gap-4 mb-6">
            <Shield size={18} className="text-rose-400" />
            <h3 className="font-bold">Account</h3>
          </div>

          <Button
            variant="danger"
            onClick={handleLogout}
            loading={isLoggingOut}
            icon={<LogOut size={16} />}
          >
            Sign Out
          </Button>
        </Card>
      )}
    </div>
  );
}
